/**
 * O contador rolante do template.
 *
 * Cada casa é uma janela com `overflow: clip` e, dentro dela, uma fita de doze
 * algarismos: o dígito final, os dez algarismos de 0 a 9, e o dígito final de
 * novo. A fita desliza de ponta a ponta — de 0% a -91,666% (onze doze avos) — e
 * o que se vê é o número girando uma volta completa antes de parar onde deveria.
 *
 * A fita começar e terminar no mesmo algarismo é o detalhe que faz isto ser
 * seguro: em repouso, sem JavaScript nenhum, a janela mostra o dígito certo. A
 * animação é enfeite por cima de um número que já está correto, e não a única
 * coisa que o coloca no lugar.
 *
 * As classes e as medidas são as do template (`.about-counter-1`,
 * `.streaming-counter-1`), então o desenho vem da folha portada — aqui mora só
 * o comportamento.
 */

import { useRef } from 'react';
import { gsap, useGSAP, ScrollTrigger, EASE } from '@/lib/motion';

/** Onze doze avos: a fita tem 12 quadros e para no último. */
const FIM = -(11 / 12) * 100;

const ALGARISMOS = [0, 1, 2, 3, 4, 5, 6, 7, 8, 9];

type Variante = 'sobre' | 'transmissao';

const CLASSES: Record<Variante, { linha: string; janela: string; fita: string; numero: string }> = {
  sobre: {
    linha: 'about-counter-items',
    janela: 'about-counter-1',
    fita: 'about-counter-1-items',
    numero: 'about-counter-text',
  },
  transmissao: {
    linha: 'streaming-counter-flex',
    janela: 'streaming-counter-1',
    fita: 'streaming-counter-items',
    numero: 'streaming-counter-no',
  },
};

interface Props {
  /** O valor final. Só a parte inteira entra. */
  valor: number;
  variante?: Variante;
  /** Zeros à esquerda, quando o desenho pede largura fixa. */
  minimoDeCasas?: number;
  /** O `+` de "150+", colado no número. */
  sufixo?: string;
  /** Classe extra na janela — o template usa `white` para inverter a tinta. */
  classeDoNumero?: string;
}

export function Contador({
  valor,
  variante = 'sobre',
  minimoDeCasas = 1,
  sufixo,
  classeDoNumero,
}: Props) {
  const raiz = useRef<HTMLDivElement>(null);
  const casas = String(Math.max(0, Math.round(valor))).padStart(minimoDeCasas, '0').split('');
  const c = CLASSES[variante];

  useGSAP(
    () => {
      const fitas = gsap.utils.toArray<HTMLElement>('[data-fita]', raiz.current);
      if (fitas.length === 0) return;

      /*
       * As casas partem juntas e param em cascata da esquerda para a direita.
       * Se todas parassem no mesmo quadro, o conjunto leria como uma imagem
       * trocando; com o atraso ele lê como um mecanismo — que é a diferença
       * entre um número que apareceu e um número que foi contado.
       */
      gsap.fromTo(
        fitas,
        { yPercent: 0 },
        {
          yPercent: FIM,
          duration: 1.4,
          ease: EASE.signature,
          stagger: 0.08,
          scrollTrigger: { trigger: raiz.current, start: 'top 90%', once: true },
        },
      );

      return () => {
        ScrollTrigger.getAll()
          .filter((t) => t.trigger === raiz.current)
          .forEach((t) => t.kill());
      };
    },
    { scope: raiz, dependencies: [casas.join('')], revertOnUpdate: true },
  );

  const numero = `${c.numero} ${classeDoNumero ?? ''}`.trim();

  return (
    <div ref={raiz} className={c.linha}>
      {/*
        O leitor de tela recebe o número escrito, e só ele. Sem isto, ele leria
        "1 0 1 2 3 4 5 6 7 8 9 1" por casa — a fita inteira em vez do valor.
      */}
      <span className="sr-only">{`${casas.join('')}${sufixo ?? ''}`}</span>

      {casas.map((digito, indice) => (
        <div key={indice} aria-hidden className={`${c.janela} _01`}>
          <div data-fita className={c.fita}>
            {/* o dígito final abre a fita… */}
            <div className={numero}>{digito}</div>
            {/* …os dez algarismos passam… */}
            {ALGARISMOS.map((a) => (
              <div key={a} className={numero}>
                {a}
              </div>
            ))}
            {/* …e ele fecha, para a parada cair no valor certo. */}
            <div className={numero}>{digito}</div>
          </div>
        </div>
      ))}

      {sufixo && (
        <div aria-hidden className={numero}>
          {sufixo}
        </div>
      )}
    </div>
  );
}
